/**
 * @file Photo Type Validation Schema
 * @description 로딩 사진 타입 유효성 검사 유틸리티
 * @see docs/plans/loading/05-implementation.md - Section 33
 */

import {
  CUSTOM_TYPE_PREFIX,
  PHOTO_TYPE_CONFIG,
  TYPE_CODE_REGEX,
} from '@/src/store/apis/photoType/interface';

/**
 * 허용 문자 패턴: 영문, 숫자, 특수문자(/ - _), 공백
 * @description Only alphanumeric characters and special characters (/ - _) are allowed.
 */
const ALLOWED_CHARS_REGEX = /^[a-zA-Z0-9\s/\-_]*$/;

/**
 * 제목 유효성 검사 함수
 *
 * @description 개별 필드 검사에 사용
 * @param value - 검사할 제목 값
 * @returns 에러 메시지 키 또는 null (유효한 경우)
 */
export const validateTitle = (value: string): string | null => {
  if (!value || value.trim().length === 0) {
    return 'photoType:validation.titleRequired';
  }
  if (value.length < PHOTO_TYPE_CONFIG.TITLE_MIN_LENGTH) {
    return 'photoType:validation.titleMin';
  }
  if (value.length > PHOTO_TYPE_CONFIG.TITLE_MAX_LENGTH) {
    return 'photoType:validation.titleMax';
  }
  if (!ALLOWED_CHARS_REGEX.test(value)) {
    return 'photoType:validation.invalidChars';
  }
  return null;
};

/**
 * 부제목 유효성 검사 함수
 *
 * @description 개별 필드 검사에 사용 (선택적)
 * @param value - 검사할 부제목 값
 * @returns 에러 메시지 키 또는 null (유효한 경우)
 */
export const validateSubtitle = (value: string | null | undefined): string | null => {
  if (!value) return null;

  if (value.length > PHOTO_TYPE_CONFIG.SUBTITLE_MAX_LENGTH) {
    return 'photoType:validation.subtitleMax';
  }
  if (!ALLOWED_CHARS_REGEX.test(value)) {
    return 'photoType:validation.invalidChars';
  }
  return null;
};

export const validateTypeCode = (value: string): string | null => {
  if (!value || value.trim().length === 0) {
    return 'photoType:validation.typeCodeRequired';
  }
  if (value.length < PHOTO_TYPE_CONFIG.TYPE_CODE_MIN_LENGTH) {
    return 'photoType:validation.typeCodeMin';
  }
  if (value.length > PHOTO_TYPE_CONFIG.TYPE_CODE_MAX_LENGTH) {
    return 'photoType:validation.typeCodeMax';
  }
  if (!TYPE_CODE_REGEX.test(value)) {
    return 'photoType:validation.typeCodeInvalidFormat';
  }
  return null;
};

export interface PhotoTypeSlotFormField {
  typeCode: string;
  title: string;
  subTitle: string | null;
  isActive: boolean;
  displayOrder: number;
}

const isCustomTypeCode = (typeCode?: string): boolean => {
  return typeCode?.startsWith(CUSTOM_TYPE_PREFIX) ?? false;
};

/**
 * Custom Photo Type 최대 개수
 */
export const MAX_CUSTOM_NUMBER = 15;

/**
 * 다음 사용 가능한 Custom 타입 코드 생성
 *
 * @description 기존 슬롯에서 사용 중인 CUSTOM_N 번호를 확인하고 다음 번호 반환
 * @param existingTypeCodes - 기존 타입 코드 배열
 * @returns 다음 사용 가능한 CUSTOM_N 코드 또는 null (최대 개수 초과 시)
 *
 * @example
 * generateNextCustomTypeCode(['CUSTOM_1', 'CUSTOM_3']) // 'CUSTOM_2'
 * generateNextCustomTypeCode(['CUSTOM_1', 'CUSTOM_2', ..., 'CUSTOM_8']) // null
 */
export const generateNextCustomTypeCode = (
  existingTypeCodes: string[]
): string | null => {
  const usedNumbers = existingTypeCodes
    .map((code) => {
      const match = code.match(/^CUSTOM_(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);

  let nextNumber = 1;
  while (usedNumbers.includes(nextNumber) && nextNumber <= MAX_CUSTOM_NUMBER) {
    nextNumber++;
  }

  if (nextNumber > MAX_CUSTOM_NUMBER) {
    return null;
  }

  return `${CUSTOM_TYPE_PREFIX}${nextNumber}`;
};

/**
 * 전체 슬롯 배열 유효성 검사
 *
 * @param slots - 검사할 슬롯 배열
 * @returns 에러 메시지 키 배열 (유효한 경우 빈 배열)
 */
export const validateSlots = (slots: PhotoTypeSlotFormField[]): string[] => {
  const errors: string[] = [];

  // 최소 슬롯 개수 검사
  if (slots.length < PHOTO_TYPE_CONFIG.MIN_SLOTS) {
    errors.push('photoType:validation.minSlots');
  }

  // 최대 슬롯 개수 검사
  if (slots.length > PHOTO_TYPE_CONFIG.MAX_SLOTS) {
    errors.push('photoType:validation.maxSlots');
  }

  // 기본 유형 중복 검사
  const defaultTypes = slots
    .filter((s) => !isCustomTypeCode(s.typeCode))
    .map((s) => s.typeCode);
  if (new Set(defaultTypes).size !== defaultTypes.length) {
    errors.push('photoType:validation.duplicateTypes');
  }

  // 최소 활성 슬롯 검사
  const activeCount = slots.filter((s) => s.isActive).length;
  if (activeCount < PHOTO_TYPE_CONFIG.MIN_SLOTS) {
    errors.push('photoType:validation.minActiveSlots');
  }

  return errors;
};
